<script setup lang="ts">
/**
 * Undo, redo and save for the `quickAccess` slot of NxOfficeRibbon. It renders
 * the buttons without a wrapper, so they are direct children of the ribbon's
 * own toolbar and a host can put a command of its own in front of them.
 */
import NxOfficeIcon from './NxOfficeIcon.vue';
import NxIconButton from '../IconButton/NxIconButton.vue';
import '../office-view.css';

const props = defineProps<{
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string;
  redoLabel: string;
  saveLabel: string;
  /**
   * Keep the pointer from taking focus on Save too, so focus stays in the
   * document. Opt-in: a press that does not blur the focused field also does
   * not commit a value that field only takes on blur / change — a host that
   * sets this must commit pending edits itself before saving (Calc does).
   */
  keepFocusOnSave?: boolean;
}>();

const emit = defineEmits<{ undo: []; redo: []; save: [] }>();

function onSaveMouseDown(event: MouseEvent): void {
  if (props.keepFocusOnSave) event.preventDefault();
}
</script>

<template>
  <!-- Undo and redo keep the pointer from taking focus: the command has to
       reach the document that is being edited, not the button. Save does so
       only with `keepFocusOnSave` — see the prop. -->
  <NxIconButton
    class="nx-office-quick-access__button"
    height="s"
    :title="undoLabel"
    :disabled="!canUndo"
    @mousedown.prevent
    @click="emit('undo')"
  >
    <NxOfficeIcon name="undo" />
  </NxIconButton>
  <NxIconButton
    class="nx-office-quick-access__button"
    height="s"
    :title="redoLabel"
    :disabled="!canRedo"
    @mousedown.prevent
    @click="emit('redo')"
  >
    <NxOfficeIcon name="redo" />
  </NxIconButton>
  <NxIconButton
    class="nx-office-quick-access__button"
    height="s"
    :title="saveLabel"
    @mousedown="onSaveMouseDown"
    @click.prevent="emit('save')"
  >
    <NxOfficeIcon name="save" />
  </NxIconButton>
</template>
